import { useState, useEffect } from 'react';
import { isColorDark, hexToRgba, resolveLinkProps } from '../utils';

export default function CookieConsentSection({ block = {}, primaryColor, secondaryColor, isPreview }) {
    const { config = {}, content = {} } = block;
    const [visible, setVisible] = useState(!!isPreview);
    const [showManage, setShowManage] = useState(false);
    const [consents, setConsents] = useState({});

    const accent = primaryColor || '#2563eb';
    const variant = config.variant || 'bar';
    const position = config.position || 'bottom';
    const categories = content.categories || [];

    useEffect(() => {
        if (isPreview) return;
        const saved = localStorage.getItem('cookie_consent');
        if (!saved) {
            setVisible(true);
        }
        // Initialize consents from categories
        const initial = {};
        categories.forEach(cat => {
            initial[cat.key] = cat.required ? true : false;
        });
        setConsents(initial);
    }, []);

    const accept = () => {
        if (!isPreview) localStorage.setItem('cookie_consent', 'all');
        setVisible(false);
    };

    const reject = () => {
        if (!isPreview) localStorage.setItem('cookie_consent', 'essential');
        setVisible(false);
    };

    const savePrefs = () => {
        if (!isPreview) localStorage.setItem('cookie_consent', JSON.stringify(consents));
        setVisible(false);
    };

    const toggleConsent = (key, required) => {
        if (required) return;
        setConsents(prev => ({ ...prev, [key]: !prev[key] }));
    };

    if (!visible) return null;

    const title = content.title || 'Cookies';
    const message = content.message || 'Utilizamos cookies para mejorar tu experiencia en nuestro sitio web.';
    const acceptLabel = content.accept_label || 'Aceptar todo';
    const rejectLabel = content.reject_label || 'Rechazar';
    const manageLabel = content.manage_label || 'Configurar';
    const privacyUrl = content.privacy_url;
    const accentDark = isColorDark(accent);

    // ── Modal variant ──
    if (variant === 'modal') {
        return (
            <div className={`${isPreview ? 'relative w-full' : 'fixed inset-0 z-[9999] bg-black/50'} flex items-center justify-center p-4`}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-[fadeInUp_0.4s_ease-out]"
                    onClick={e => e.stopPropagation()}>
                    <div className="p-6 md:p-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: hexToRgba(accent, 0.1) }}>
                                <svg className="w-5 h-5" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{message}</p>

                        {/* Category management */}
                        {showManage && categories.length > 0 && (
                            <div className="mt-5 space-y-3 pt-5 border-t border-gray-200">
                                {categories.map((cat, i) => (
                                    <div key={i} className="flex items-start justify-between gap-4 p-3 rounded-lg bg-gray-50">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-gray-900">{cat.label}</span>
                                                {cat.required && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">Requerida</span>}
                                            </div>
                                            {cat.description && <p className="text-xs text-gray-500 mt-0.5">{cat.description}</p>}
                                        </div>
                                        <button onClick={() => toggleConsent(cat.key, cat.required)}
                                            className={`relative w-11 h-6 rounded-full shrink-0 transition-colors duration-200 ${cat.required ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                            style={{ backgroundColor: consents[cat.key] ? accent : '#d1d5db' }}>
                                            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${consents[cat.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {privacyUrl && (
                            <a {...resolveLinkProps(privacyUrl)} className="inline-flex items-center gap-1 text-xs mt-4 hover:underline transition-colors" style={{ color: accent }}>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                Politica de privacidad
                            </a>
                        )}

                        <div className="flex flex-wrap gap-3 mt-6">
                            <button onClick={accept}
                                className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 hover:scale-105 shadow-lg"
                                style={{ backgroundColor: accent, color: accentDark ? '#ffffff' : '#111827' }}>
                                {acceptLabel}
                            </button>
                            <button onClick={reject}
                                className="px-6 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all hover:opacity-80"
                                style={{ color: accent, borderColor: accent }}>
                                {rejectLabel}
                            </button>
                            {categories.length > 0 && !showManage && (
                                <button onClick={() => setShowManage(true)}
                                    className="px-6 py-2.5 rounded-lg text-sm font-semibold text-gray-600 transition-all hover:opacity-80">
                                    {manageLabel}
                                </button>
                            )}
                            {showManage && (
                                <button onClick={savePrefs}
                                    className="px-6 py-2.5 rounded-lg text-sm font-semibold text-gray-600 transition-all hover:opacity-80 border border-gray-300">
                                    Guardar preferencias
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <style>{`
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    // ── Bar variant (default) ──
    const barPosition = isPreview
        ? 'relative w-full'
        : position === 'bottom-left'
            ? 'fixed bottom-6 left-6 z-[9999] max-w-md'
            : 'fixed bottom-0 left-0 right-0 z-[9999]';

    return (
        <div className={barPosition}>
            <div className={`${position === 'bottom-left' ? 'rounded-2xl shadow-2xl' : 'shadow-[0_-4px_20px_rgba(0,0,0,0.15)]'}`}
                style={{ backgroundColor: '#111827', animation: 'slideUp 0.4s ease-out' }}>
                <div className={`${position === 'bottom-left' ? 'p-5' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5'}`}>
                    <div className={`flex flex-col ${position === 'bottom' ? 'md:flex-row md:items-center' : ''} gap-4`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <svg className="w-5 h-5 text-amber-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 7.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM8.5 8c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-2 5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5z" />
                                </svg>
                                <h4 className="text-sm font-bold text-white">{title}</h4>
                            </div>
                            <p className="text-sm text-gray-400 leading-relaxed">{message}</p>
                            {privacyUrl && (
                                <a {...resolveLinkProps(privacyUrl)} className="inline-flex items-center gap-1 text-xs text-gray-500 mt-1 hover:text-gray-300 transition-colors">
                                    Politica de privacidad
                                </a>
                            )}
                        </div>

                        {/* Category management */}
                        {showManage && categories.length > 0 && (
                            <div className={`space-y-2 ${position === 'bottom' ? 'md:max-w-sm' : ''} w-full`}>
                                {categories.map((cat, i) => (
                                    <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/5">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs font-semibold text-white">{cat.label}</span>
                                                {cat.required && <span className="text-[9px] px-1 py-0.5 rounded bg-white/10 text-gray-400">Requerida</span>}
                                            </div>
                                            {cat.description && <p className="text-[11px] text-gray-500 mt-0.5">{cat.description}</p>}
                                        </div>
                                        <button onClick={() => toggleConsent(cat.key, cat.required)}
                                            className={`relative w-9 h-5 rounded-full shrink-0 transition-colors duration-200 ${cat.required ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                            style={{ backgroundColor: consents[cat.key] ? accent : '#4b5563' }}>
                                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${consents[cat.key] ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                            <button onClick={accept}
                                className="px-5 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90 hover:scale-105"
                                style={{ backgroundColor: accent, color: accentDark ? '#ffffff' : '#111827' }}>
                                {acceptLabel}
                            </button>
                            <button onClick={reject}
                                className="px-5 py-2 rounded-lg text-sm font-semibold border border-gray-600 text-gray-300 transition-all hover:bg-white/5">
                                {rejectLabel}
                            </button>
                            {categories.length > 0 && !showManage && (
                                <button onClick={() => setShowManage(true)}
                                    className="px-5 py-2 rounded-lg text-sm font-semibold text-gray-400 transition-all hover:text-gray-200">
                                    {manageLabel}
                                </button>
                            )}
                            {showManage && (
                                <button onClick={savePrefs}
                                    className="px-5 py-2 rounded-lg text-sm font-semibold text-gray-300 border border-gray-600 transition-all hover:bg-white/5">
                                    Guardar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(100%); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
